/**
 * Derived from AudioCN (https://github.com/audiocn/ui) at commit
 * 42f863ec333c6912c421d1e9d6d7ce7850ce2680.
 * Source file: components/ui/audio-player.tsx (AudioPlayerPlay,
 * AudioPlayerSeek, AudioPlayerTime).
 * License: MIT, Copyright (c) 2026 OrcDev. See components/ui/audiocn/LICENSE.md.
 *
 * Only the markup and styling structure of those three parts is taken. The
 * AudioPlayer context, its controller, the use-audio-player hook and Base UI
 * are not: every part here is presentational and takes its state as props.
 * Playback stays on our useAudioPlayer, audio-coordinator and
 * AudioPlayerContext. Nothing here loads, fetches or decodes audio.
 *
 * Local changes:
 *   PlayerPlay (from AudioPlayerPlay)
 *   - Keeps the part's shape: a type="button" element with a data-slot,
 *     data-playing / data-loading state attributes and a children render
 *     function that receives { playing, loading }.
 *   - A11y follows our contract instead of AudioCN's: a stable "Play <title>"
 *     name from playToggleA11y(), aria-pressed as the only state channel,
 *     aria-busy while loading, and never disabled (AudioCN flipped the name
 *     to "Pause" and disabled the button while idle).
 *   - No paint of its own. TPlay (components/ui/tls/t-play.tsx) paints
 *     AudioCN's solid disc onto it in our tokens: ink at rest, accent while
 *     playing, Phosphor fill glyphs. The shadcn primary, hover fill and ring-3
 *     focus are gone.
 *   PlayerSeek (from AudioPlayerSeek)
 *   - Keeps the part's drag model: a local drag value overrides the playhead
 *     while the pointer is down, and the seek commits once on release.
 *   - The Base UI slider, rounded track, buffered bar and round thumb are
 *     replaced by the vendored Waveform in interactive mode (§03.3: "the strip
 *     is the scrub bar, there is no separate track"). It draws stored peaks
 *     only: one-pixel ticks on a four-pixel pitch, an accent playhead, radius 0.
 *   - Arrow keys step 0.1 s and Shift+arrow 0.5 s (was 5 s and 15 s).
 *   - Named "Seek <title>"; the value text reads in the same format as the
 *     readout beside the strip ("0:01.20 of 0:03.40").
 *   - The strip pans vertically on touch, so a swipe over it still scrolls
 *     the page; a press that ends without a seek (cancel, lost capture, no
 *     button down) drops its drag value, so the playhead follows playback.
 *   - An SVG copy of the ticks is server-rendered under the canvas, so the
 *     strip is in the first paint and with JavaScript off.
 *   PlayerTime (from AudioPlayerTime)
 *   - Keeps the reserved width (as wide as the longest time it can show, so
 *     it never shifts), its end alignment and the current / remaining /
 *     duration types.
 *   - Archivo tabular figures instead of font-mono (owner ruling B,
 *     2026-08-14), set like the sound row's facts: the body-sm step, weight
 *     600, meta ink. Whole seconds by default, §01.9's `0:00.62` where a
 *     scrubber needs precision.
 */

"use client";

import { useEffect, useId, useMemo, useState } from "react";
import type { ComponentProps, CSSProperties, ReactNode } from "react";

import {
  Waveform,
  WaveformCanvas,
  WaveformCursor,
} from "@/components/ui/audiocn/waveform";
import { playToggleA11y } from "@/lib/a11y/play-toggle";
import { formatTime } from "@/lib/audiocn/time";
import { cn } from "@/lib/utils";

export interface PlayerPlayState {
  playing: boolean;
  loading: boolean;
}

export interface PlayerPlayProps
  extends Omit<
    ComponentProps<"button">,
    "children" | "disabled" | "type" | "aria-label" | "aria-pressed" | "aria-busy"
  > {
  playing: boolean;
  /** True while the track resolves or buffers. Reaches aria-busy only. */
  loading?: boolean;
  /** Subject of the stable name, normally the track title. */
  title?: string;
  children?: ReactNode | ((state: PlayerPlayState) => ReactNode);
}

export const PlayerPlay = ({
  playing,
  loading = false,
  title,
  children,
  ...props
}: PlayerPlayProps) => {
  const content =
    typeof children === "function" ? children({ loading, playing }) : children;
  return (
    <button
      type="button"
      data-slot="audio-player-play"
      data-playing={playing ? "" : undefined}
      data-loading={loading ? "" : undefined}
      {...props}
      {...playToggleA11y({ playing, title, loading })}
    >
      {content}
    </button>
  );
};

/**
 * Stored peaks arrive as 0..1, or 0..100 on a few older rows. Scaled so the
 * loudest moment of the clip reaches the top of the strip: a quiet sound still
 * draws a readable shape, and the shape itself is unchanged.
 */
const normalisePeaks = (
  peaks: ArrayLike<number> | null | undefined
): number[] | null => {
  if (!peaks || peaks.length === 0) return null;
  const values = Array.from(peaks, (value) =>
    Number.isFinite(value) ? Math.max(0, value) : 0
  );
  const max = Math.max(...values);
  if (max <= 0) return null;
  return values.map((value) => value / max);
};

/**
 * The strip as server HTML, so the ticks are in the first paint and with
 * JavaScript off. The envelope is the peaks stretched to the strip's width;
 * a user-space pattern cuts it into the canvas's one-unit ticks on a
 * four-unit pitch, so the canvas draws over the same pixels when it takes
 * over after hydration.
 */
const StripFallback = ({ levels }: { levels: number[] }) => {
  // url(#…) references want a plain id; useId can include « » or colons.
  const id = `strip${useId().replace(/[^\w-]/g, "")}`;
  const envelope = levels
    .map((level, index) => `M${index} ${1 - level}h1V1h-1Z`)
    .join("");
  return (
    <svg
      aria-hidden
      className="pointer-events-none absolute inset-0 size-full text-(--waveform)"
      data-slot="waveform-fallback"
    >
      <defs>
        <pattern height="1" id={`${id}-ticks`} patternUnits="userSpaceOnUse" width="4">
          <rect fill="currentColor" height="1" width="1" />
        </pattern>
        <mask id={`${id}-envelope`}>
          <svg height="100%" preserveAspectRatio="none" viewBox={`0 0 ${levels.length} 1`} width="100%">
            <path d={envelope} fill="white" />
          </svg>
        </mask>
      </defs>
      <rect fill={`url(#${id}-ticks)`} height="100%" mask={`url(#${id}-envelope)`} width="100%" />
    </svg>
  );
};

/** Where the strip is painted, which decides its two inks. */
export type PlayerGround = "canvas" | "hero";

const GROUND_CLASS: Record<PlayerGround, string> = {
  canvas:
    "[--waveform:var(--tls-fg-faint)] [--waveform-progress:var(--tls-brand)] [--waveform-cursor:var(--tls-brand)]",
  hero:
    "[--waveform:var(--tls-hero-wave-idle)] [--waveform-progress:var(--tls-hero-signal)] [--waveform-cursor:var(--tls-hero-signal)]",
};

/**
 * §01.9: hundredths where precision matters (the sound page and the tray
 * scrubbers), whole seconds everywhere else, the way the sound rows read.
 * Tenths are for a slider's value text beside a whole-second readout, so each
 * 0.1 s arrow press announces a new value.
 */
export type TimePrecision = 0 | 1 | 2;

const formatReadout = (seconds: number, decimals: TimePrecision) =>
  formatTime(seconds, { decimals });

export interface PlayerSeekProps
  extends Omit<ComponentProps<"div">, "onChange" | "defaultValue" | "title"> {
  /** Stored waveform_data peaks. Never decoded from audio. */
  peaks: ArrayLike<number> | null | undefined;
  duration: number;
  currentTime: number;
  /** The track title, for the slider's "Seek <title>" name. */
  title: string;
  /** Fires once per seek: on pointer release, or on each key press. */
  onSeekCommit?: (time: number) => void;
  /** False draws the strip only, with no slider and no tab stop. */
  interactive?: boolean;
  /** Draw the playhead. Off while this track is not the current one. */
  playhead?: boolean;
  ground?: PlayerGround;
  /** Precision of the value text; match the readout beside the strip. */
  decimals?: TimePrecision;
  /**
   * Keep the server-drawn SVG and mount no canvas, so the strip costs no frame
   * loop. For resting strips in long lists; implies `interactive={false}`.
   */
  still?: boolean;
}

export const PlayerSeek = ({
  peaks,
  duration,
  currentTime,
  title,
  onSeekCommit,
  interactive = true,
  playhead = true,
  ground = "canvas",
  decimals = 2,
  still = false,
  className,
  style,
  ...props
}: PlayerSeekProps) => {
  const [dragValue, setDragValue] = useState<number | null>(null);
  // The server fallback stays until the canvas has had a frame to paint.
  const [painted, setPainted] = useState(false);
  const levels = useMemo(() => normalisePeaks(peaks), [peaks]);
  const shownTime = dragValue ?? currentTime;

  useEffect(() => {
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setPainted(true));
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, []);

  const sliderProps = interactive && !still
    ? {
        "aria-label": `Seek ${title}`,
        "aria-valuetext": `${formatReadout(shownTime, decimals)} of ${formatReadout(duration, decimals)}`,
      }
    : { "aria-hidden": true };

  return (
    <Waveform
      {...sliderProps}
      barGap={3}
      barWidth={1}
      className={cn("h-14", GROUND_CLASS[ground], className)}
      currentTime={shownTime}
      data-slot="audio-player-seek"
      duration={duration}
      interactive={interactive && !still}
      onSeek={(time) => setDragValue(time)}
      onSeekCancel={() => setDragValue(null)}
      onSeekCommitted={(time) => {
        setDragValue(null);
        onSeekCommit?.(time);
      }}
      peaks={levels}
      // Inline, so it outranks the vendored touch-none: a vertical swipe over
      // the strip still scrolls the page.
      style={{ touchAction: "pan-y", ...style }}
      {...props}
    >
      {levels && (still || !painted) ? <StripFallback levels={levels} /> : null}
      {still ? null : <WaveformCanvas />}
      {playhead ? <WaveformCursor /> : null}
    </Waveform>
  );
};

export type PlayerTimeType = "current" | "remaining" | "duration";

export interface PlayerTimeProps extends ComponentProps<"span"> {
  /** Default `current`. */
  type?: PlayerTimeType;
  currentTime?: number;
  duration: number;
  /** 2 on the sound page and tray scrubbers, 0 (whole seconds) elsewhere. */
  decimals?: TimePrecision;
  format?: (seconds: number, type: PlayerTimeType) => string;
  /** Take font and ink from the parent, for a readout set inside a line. */
  inheritFont?: boolean;
}

export const PlayerTime = ({
  type = "current",
  currentTime = 0,
  duration,
  decimals = 0,
  format,
  inheritFont = false,
  className,
  style,
  ...props
}: PlayerTimeProps) => {
  const render =
    format ??
    ((value: number, kind: PlayerTimeType) => {
      // A sub-second clip reads 0:01 in whole seconds, as fmtDuration does in
      // the rows, so a real sound never shows a 0:00 length.
      const shown =
        decimals === 0 && kind === "duration" && value > 0 && value < 1 ? 1 : value;
      const text = formatReadout(shown, decimals);
      return kind === "remaining" ? `−${text}` : text;
    });
  // As wide as the longest time this track can show, so it never shifts.
  const timeWidth = Math.max(render(0, type).length, render(duration, type).length);
  let seconds = currentTime;
  if (type === "duration") {
    seconds = duration;
  } else if (type === "remaining") {
    seconds = Math.max(0, duration - currentTime);
  }
  return (
    <span
      className={cn(
        "inline-block min-w-(--audio-player-time-width) whitespace-nowrap text-end tabular-nums",
        !inheritFont && "text-sm font-semibold text-(--tls-fg-subtle)",
        className
      )}
      data-slot="audio-player-time"
      data-type={type}
      style={
        {
          "--audio-player-time-width": `${timeWidth}ch`,
          ...style,
        } as CSSProperties
      }
      {...props}
    >
      {render(seconds, type)}
    </span>
  );
};
